import type React from 'react'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { type UserRoleType, supabase } from '../lib/supabase'
import { useAuth } from './AuthContext'

interface RoleContextType {
  currentRole: UserRoleType | null
  availableRoles: UserRoleType[]
  loading: boolean
  switchRole: (role: UserRoleType) => Promise<{ error?: Error | null }>
  addRole: (role: UserRoleType) => Promise<{ error?: Error | null }>
  refreshRoles: () => Promise<void>
}

const RoleContext = createContext<RoleContextType | undefined>(undefined)

export const useRole = () => {
  const context = useContext(RoleContext)
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider')
  }
  return context
}

interface RoleProviderProps {
  children: React.ReactNode
}

export const RoleProvider: React.FC<RoleProviderProps> = ({ children }) => {
  const { user } = useAuth()
  const [currentRole, setCurrentRole] = useState<UserRoleType | null>(null)
  const [availableRoles, setAvailableRoles] = useState<UserRoleType[]>([])
  const [loading, setLoading] = useState(true)

  const fetchUserRoles = useCallback(async () => {
    if (!user) {
      setCurrentRole(null)
      setAvailableRoles([])
      setLoading(false)
      return
    }

    try {
      // Get all available roles for the user
      const { data: userRoles, error } = await supabase.from('user_roles').select('role').eq('user_id', user.id)

      if (error) {
        console.error('Error fetching user roles:', error)
        // Fallback to default athlete role
        setAvailableRoles(['athlete'])
        setCurrentRole('athlete')
        setLoading(false)
        return
      }

      const roles = userRoles?.map((ur) => ur.role as UserRoleType) || ['athlete']
      setAvailableRoles(roles)

      // Set default current role - prefer stored value or default to first available role
      const storedRole = localStorage.getItem(`current_role_${user.id}`) as UserRoleType
      if (storedRole && roles.includes(storedRole)) {
        console.log('[RoleContext] Using selected role:', storedRole)
        setCurrentRole(storedRole)
      } else {
        const defaultRole = roles.includes('athlete') ? 'athlete' : roles[0]
        console.log('[RoleContext] Using default role:', defaultRole)
        setCurrentRole(defaultRole)
        localStorage.setItem(`current_role_${user.id}`, defaultRole)
      }
    } catch (error) {
      console.error('Exception fetching user roles:', error)
      setAvailableRoles(['athlete'])
      setCurrentRole('athlete')
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    fetchUserRoles()
  }, [fetchUserRoles])

  const switchRole = async (role: UserRoleType) => {
    if (!user) {
      return { error: new Error('User not authenticated') }
    }

    if (!availableRoles.includes(role)) {
      return { error: new Error(`User does not have the ${role} role`) }
    }

    try {
      // Update local state
      setCurrentRole(role)

      // Store in localStorage for persistence
      localStorage.setItem(`current_role_${user.id}`, role)

      return { error: null }
    } catch (error) {
      console.error('Exception switching role:', error)
      return { error: error as Error }
    }
  }

  const addRole = async (role: UserRoleType) => {
    if (!user) {
      return { error: new Error('User not authenticated') }
    }

    try {
      // Call the database function to add a role
      const { error } = await supabase.rpc('add_user_role', {
        target_role: role,
      })

      if (error) {
        console.error('Error adding role:', error)
        return { error }
      }

      // Refresh roles
      await fetchUserRoles()

      return { error: null }
    } catch (error) {
      console.error('Exception adding role:', error)
      return { error: error as Error }
    }
  }

  const refreshRoles = async () => {
    await fetchUserRoles()
  }

  const value: RoleContextType = {
    currentRole,
    availableRoles,
    loading,
    switchRole,
    addRole,
    refreshRoles,
  }

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>
}
